import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { ClaimColumn } from '../../../models/api/view/claim-view/claim-column';
import { ClaimViewResponse } from '../../../models/api/view/claim-view/claim-view-response';
import { SaveClaimView } from '../../../models/api/view/claim-view/save-claim-view';
import { CasePipe } from '../../../pipes/case.pipe';
import { Format } from '../../../pipes/format.pipe';
import { Snackbar } from '../../../services/snackbar/snackbar.service';
import { ClaimViewColumnService } from '../../../services/view/claim-view-column.service';
import { BaseComponent } from '../../base/base.component';
import { ButtonComponent } from '../../core/buttons/button.component';
import { ExportFormat } from '../../core/buttons/export-button.component';
import {
  PickListComponent,
  RemoveItemsEvent,
} from '../../core/controls/pick-list.component';
import { ClaimLabelComponent } from '../../core/labels/claim-label.component';
import { ActionRowComponent } from '../../core/panels/action-row.component';
import { ColumnComponent } from '../../core/table/column.component';
import { TableComponent } from '../../core/table/table.component';
/**
 * Claim view displays claims for the watchlist, history and search pages.
 */
@Component({
  selector: 'app-claim-view',
  imports: [
    TableComponent,
    ColumnComponent,
    RouterModule,
    ActionRowComponent,
    ButtonComponent,
    CommonModule,
    ClaimLabelComponent,
    CasePipe,
    PickListComponent,
  ],
  template: `
    @if (claimView && columnView) {
      @if (showPickList) {
        <div
          class="tw-w-full tw-overflow-x-auto tw-min-w-0"
          tabindex="0"
          role="caption"
          [attr.aria-labelledby]="picklistCaptionId"
        >
          <div class="tw-min-w-fit">
            <div class="tw-py-4">
              <h2 [id]="picklistCaptionId">Customize display columns</h2>
              <div class="tw-py-4">
                Customizations selected will be applied to Search Results,
                History and Watch List screens.
              </div>
            </div>
            <app-pick-list
              [sourceControl]="availableControl"
              [targetControl]="selectedControl"
              labelName="displayText"
              keyName="propertyName"
              (beforeRemoveItems)="onBeforeRemoveItems($event)"
              [autoFocusFilter]="true"
            />

            <div class="tw-pt-4">
              <app-action-row>
                <div left1 class="tw-text-md tw-max-w-[500px] tw-pb-4">
                  **These fields will be represented as rows as opposed to
                  additional columns.
                </div>
                <app-button
                  left1
                  emphasis="Low"
                  (click)="restoreDefault()"
                  label="Restore defaults"
                />
                <app-button
                  right1
                  emphasis="High"
                  (click)="saveCustomizedView()"
                  label="Save"
                />
                <app-button
                  right2
                  emphasis="Low"
                  (click)="cancelCustomizedView()"
                  label="Cancel"
                />
              </app-action-row>
            </div>
          </div>
        </div>
      } @else {
        <app-table
          [dataSource]="claimView.rows"
          [persistPaginationSettings]="persistPaginationSettings"
          [tableId]="tableId"
          [showSelection]="showSelection"
          [showExport]="true"
          [showConfigure]="true"
          [showConfigureMobile]="false"
          [showRemove]="showRemove"
          [showRemoveAll]="showRemove"
          [ariaTableLabel]="claimViewAriaLabel"
          (configure)="openPickList()"
          [recordLabel]="'Claim'"
          [selectionLabel]="getClaimSelectionLabel"
          [showFilter]="true"
          [showFilterButton]="true"
          [sortField]="sortField"
          [sortOrder]="sortOrder"
          (removeRows)="onRemove($event)"
          (export)="onExport($event)"
          (paginationReady)="paginationReady.emit()"
        >
          @for (col of visibleColumns; track col.propertyName) {
            <!---->
            @if (col.propertyName === 'ClaimNumber') {
              <app-column name="fileNumber" label="Claim">
                <ng-template let-data>
                  <app-claim-label
                    [claim]="data"
                    [showWatchButton]="showWatchButton"
                  />
                </ng-template>
              </app-column>
            } @else if (col.propertyName === 'WatchStatus') {
              <!-- // do not show -->
            } @else if (
              col.fieldLength > CustomizedClaimListMaxColWidth &&
              col.isWrappable
            ) {
              <app-column
                [name]="col.propertyName | case: 'camel'"
                [label]="col.headerText"
                [fullRow]="true"
              />
            } @else if (col.propertyName === 'LossDate') {
              <app-column
                [name]="col.propertyName | case: 'camel'"
                label="Date of Loss"
                [format]="getFormat(col.propertyName)"
              />
            } @else if (col.propertyName === 'DateLastWorked') {
              <app-column
                [name]="col.propertyName | case: 'camel'"
                label="Last Day Worked"
                [format]="getFormat(col.propertyName)"
              />
            } @else if (col.propertyName === 'DateAccessed') {
              @if (showDateAccessed) {
                <app-column
                  [name]="col.propertyName | case: 'camel'"
                  [label]="col.headerText"
                  [format]="getFormat(col.propertyName)"
                />
              }
            } @else if (col.propertyName === 'SSN') {
              <app-column
                name="ssn"
                [label]="col.headerText"
                [format]="getFormat(col.propertyName)"
              />
            } @else {
              <app-column
                [name]="col.propertyName | case: 'camel'"
                [label]="col.headerText"
                [format]="getFormat(col.propertyName)"
              />
            }
          }
        </app-table>
      }
    }
  `,
  styles: ``,
})
export class ClaimViewComponent extends BaseComponent {
  private snackbar = inject(Snackbar);
  private claimViewColumnService = inject(ClaimViewColumnService);

  @Input()
  claimView: ClaimViewResponse;

  @Input()
  columnView: ClaimViewResponse;

  @Input()
  showDateAccessed = false;

  @Input()
  showSelection = false;

  @Input()
  showRemove = false;

  @Input()
  showPickList = false;

  @Input()
  claimViewAriaLabel: string;

  /** Opt-in page-size persistence (forwarded to app-table). */
  @Input()
  persistPaginationSettings = false;

  /**
   * Optional persistence key override (forwarded to app-table).
   * Claim search + Filter search share the same id (Classic SearchResults).
   */
  @Input()
  tableId: string;

  @Input()
  showWatchButton = true;

  @Input()
  sortField: string;

  @Input()
  sortOrder = 1;

  @Output()
  removeRows = new EventEmitter<{ type: 'selected' | 'all'; rows?: any[] }>();

  @Output()
  export = new EventEmitter<ExportFormat>();

  @Output()
  columnsUpdated = new EventEmitter();

  @Output()
  paginationReady = new EventEmitter<void>();

  availableControl = new FormControl<ClaimColumn[]>([]);

  selectedControl = new FormControl<ClaimColumn[]>([]);

  @ViewChild(PickListComponent)
  pickList: PickListComponent;

  originalColumns: any[] = [];
  originalUnselectedColumns: any[] = [];
  loaded = false;
  CustomizedClaimListMaxColWidth = 25;
  lockedColumns: string[] = ['ClaimNumber', 'DateAccessed'];
  lockedKeyProperty = 'propertyName';
  picklistCaptionId = 'claim-view-picklist-caption';

  get visibleColumns(): ClaimColumn[] {
    return this.columnView?.columns?.filter((col) => !col.isRestricted) ?? [];
  }

  /**
   * Returns the claim file number for the selection checkbox aria-label.
   * Used for accessibility to announce which claim is being selected.
   */
  getClaimSelectionLabel = (row: any): string => {
    return row?.fileNumber || 'claim';
  };

  private setPicklist(
    columns: ClaimColumn[],
    unselectedColumns: ClaimColumn[],
  ) {
    const unrestricted = (col: ClaimColumn) => !col.isRestricted;
    const available = [...unselectedColumns, ...columns]
      .filter(unrestricted)
      .map((col) => ({
        ...col,
        displayText: this.getDisplayText(col),
      }));
    const selected = columns.filter(unrestricted).map((col) => ({
      ...col,
      displayText: this.getDisplayText(col),
    }));
    this.availableControl.setValue(available);
    this.selectedControl.setValue(selected);
  }

  onRemove(event: { type: 'selected' | 'all'; rows?: any[] }) {
    this.removeRows.emit(event);
  }

  onExport(format: ExportFormat) {
    this.export.emit(format);
  }

  openPickList() {
    this.setPicklist(
      this.columnView.columns,
      this.columnView.unselectedColumns,
    );
    this.showPickList = true;
    this.originalColumns = [...this.columnView.columns];
    this.originalUnselectedColumns = [...this.columnView.unselectedColumns];
  }

  async saveCustomizedView() {
    const columns = this.selectedControl.value;
    const selectedColumns = columns.map((r) => r.propertyName);
    this.showPickList = false;
    const saveClaimView: SaveClaimView = {
      viewName: 'SEARCH',
      columns: selectedColumns,
    };
    if (!this.columnsChanged(columns)) {
      this.showPickList = false;
      return;
    }
    await this.api.claimView.save(saveClaimView);
    const response = await this.claimViewColumnService.refresh();
    this.updateColumnView(response.columns, response.unselectedColumns);
    this.showPickList = false;
    this.columnsUpdated.emit();
  }

  async restoreDefault() {
    await this.api.claimView.restoreDefault('SEARCH');
    const response = await this.claimViewColumnService.refresh();
    this.columnView.columns = response.columns;
    this.columnView.unselectedColumns = response.unselectedColumns;
    this.openPickList();
    this.columnsUpdated.emit();
  }

  cancelCustomizedView() {
    this.updateColumnView(this.originalColumns, this.originalUnselectedColumns);
    this.showPickList = false;
  }

  private updateColumnView(
    columns: ClaimColumn[],
    unselectedColumns: ClaimColumn[],
  ) {
    this.columnView.columns = columns;
    this.columnView.unselectedColumns = unselectedColumns;
  }

  onBeforeRemoveItems(event: RemoveItemsEvent) {
    const locked = (event.items || []).filter((item) =>
      this.lockedColumns.includes(item[this.lockedKeyProperty]),
    );
    if (locked.length > 0) {
      this.snackbar.show(
        ` ${locked
          .map((i) => i[this.lockedKeyProperty])
          .join(', ')} cannot be removed as a display column.`,
        'Error',
      );
      event.cancel = true;
      return;
    }
  }

  columnsChanged(newColumns: any[]): boolean {
    if (newColumns.length !== this.originalColumns.length) {
      return true;
    }

    for (let i = 0; i < newColumns.length; i++) {
      if (newColumns[i].propertyName !== this.originalColumns[i].propertyName) {
        return true;
      }
    }

    return false;
  }

  getDisplayText(col: any): string {
    let displayText = col.headerText;

    if (col.propertyName === 'ClaimNumber') {
      displayText = 'Claim';
    } else if (col.propertyName === 'DateLastWorked') {
      displayText = 'Last Day Worked';
    } else if (col.propertyName === 'LossDate') {
      displayText = 'Date of Loss';
    }

    if (
      col.fieldLength > this.CustomizedClaimListMaxColWidth &&
      col.isWrappable
    ) {
      displayText += '**';
    }

    return displayText;
  }

  getFormat(name: string): Format {
    switch (name) {
      case 'DateAccessed':
        return 'dt';
      case 'PtdExpense':
      case 'PtdMedical':
      case 'PtdTotal':
      case 'PtdLoss':
      case 'IncurredExpense':
      case 'IncurredMedical':
      case 'IncurredTotal':
      case 'IncurredLoss':
      case 'ReserveExpense':
      case 'ReserveMedical':
      case 'ReserveTotal':
      case 'ReserveLoss':
        return 'c';
      case 'DateCreate':
      case 'DateClosed':
      case 'DateBegin':
      case 'DateEnd':
      case 'DateBirth':
      case 'DateOpen':
      case 'DateRptByClient':
      case 'DateRptToClient':
      case 'DateLastAction':
      case 'DateClaimLastReopened':
      case 'DateRptToTPA':
      case 'DateHire':
      case 'LossDate':
      case 'DateLastWorked':
      case 'DateApprovedBegin':
      case 'DateApprovedEnd':
      case 'DateReturnedToWork':
      case 'DateAtRisk':
      case 'DateMidRange':
      case 'TrialDate':
      case 'MediationDate':
        return 'd';
      default:
        return null;
    }
  }
}
